import { Component, Input, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DragDropDirective } from './drag-drop.directive';

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * Single-file upload bound to a `FormControl<File | null>`: browse or drag & drop,
 * type/size validated before the file reaches the control. Shows the picked file
 * with a Remove button; when the control is disabled it renders read-only.
 * The control's own `required` error shows once it is touched.
 */
@Component({
  selector: 'app-file-upload-reusable',
  standalone: true,
  imports: [DragDropDirective],
  host: { class: 'block' },
  styles: [
    `
      .dropzone.drag-over {
        border-color: #6366f1;
        background-color: rgba(99, 102, 241, 0.1);
      }
    `,
  ],
  template: `
    @if (label) {
      <p class="mb-2 text-sm font-medium text-slate900">
        {{ label }}
        @if (required) {
          <span class="text-red600">*</span>
        }
      </p>
    }

    @if (file(); as picked) {
      <div class="flex items-center gap-3 rounded-lg border px-4 py-2.5" [class]="isDisabled() ? 'border-slate300 bg-slate50' : 'border-green500 bg-green50'">
        <span
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white"
          [class]="isDisabled() ? 'bg-slate500' : 'bg-green500'"
        >
          {{ extension(picked) }}
        </span>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-slate900">{{ picked.name }}</p>
          <p class="mt-0.5 text-xs text-slate500">{{ size(picked) }}</p>
        </div>
        @if (!isDisabled()) {
          <button
            type="button"
            class="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-red500 transition hover:bg-red100"
            aria-label="Remove file"
            (click)="remove()"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M5 5l10 10" />
              <path d="M15 5 5 15" />
            </svg>
          </button>
        }
      </div>
    } @else if (isDisabled()) {
      <p class="rounded-lg border border-slate300 bg-slate50 px-4 py-2.5 text-sm text-slate500">No file uploaded</p>
    } @else {
      <label
        appDragDrop
        (filesDropped)="onDrop($event)"
        class="dropzone flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-center transition"
        [class]="error() ? 'border-red500 bg-red50/40' : 'border-indigo300 bg-indigo50/40'"
      >
        <span class="flex h-8 w-8 items-center justify-center rounded-full bg-indigo100 text-indigo600">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M17 8l-5-5-5 5" />
            <path d="M12 3v12" />
          </svg>
        </span>
        <p class="text-xs text-slate600"><span class="font-semibold text-indigo600">Browse</span> or Drag &amp; drop</p>
        <p class="text-[11px] text-slate400">Formats: {{ formatsLabel }} &bull; Max file size {{ maxSizeMb }}MB</p>
        <input type="file" class="hidden" [accept]="accept" (change)="onInputChange($event)" />
      </label>
    }

    @if (error()) {
      <p class="mt-1 text-xs text-red600">{{ error() }}</p>
    }
  `,
})
export class FileUploadReusableComponent {
  @Input({ required: true }) control!: FormControl<File | null>;
  @Input() label = '';
  @Input() required = false;
  /** Same format as the input's accept attribute, e.g. ".pdf,application/pdf". */
  @Input() accept = '';
  /** Shown in the hint; defaults to the extensions in `accept`. */
  @Input() formats = '';
  @Input() maxSizeMb = 10;
  /** Message for the control's `required` error. */
  @Input() requiredMessage = 'Please upload a file.';

  private readonly validationError = signal('');

  // Read straight off the control each check: parents often toggle it with
  // `{ emitEvent: false }` (e.g. a view/edit switch), which no subscription would see.
  protected file(): File | null {
    return this.control.value ?? null;
  }

  protected isDisabled(): boolean {
    return this.control.disabled;
  }

  protected error(): string {
    if (this.control.disabled) return '';
    if (this.validationError()) return this.validationError();
    return this.control.touched && this.control.hasError('required') ? this.requiredMessage : '';
  }

  protected get formatsLabel(): string {
    if (this.formats) return this.formats;
    return this.acceptedExtensions().map((ext) => ext.slice(1).toUpperCase()).join(', ') || 'Any';
  }

  protected onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    // `input.files` is live — copy it before clearing the value (which lets the same file be re-picked).
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length) this.onFiles(files);
  }

  protected onDrop(files: FileList): void {
    this.onFiles(Array.from(files));
  }

  private onFiles(files: File[]): void {
    if (files.length > 1) {
      this.validationError.set('Please upload one file only.');
      return;
    }
    const file = files[0];
    if (!this.isAccepted(file)) {
      this.validationError.set(`Only ${this.formatsLabel} files are allowed.`);
      return;
    }
    if (file.size > this.maxSizeMb * 1024 * 1024) {
      this.validationError.set(`File must be ${this.maxSizeMb} MB or smaller.`);
      return;
    }
    this.validationError.set('');
    this.control.setValue(file);
    this.control.markAsTouched();
  }

  protected remove(): void {
    this.validationError.set('');
    this.control.setValue(null);
    this.control.markAsTouched();
  }

  protected extension(file: File): string {
    return file.name.includes('.') ? file.name.split('.').pop()!.slice(0, 4).toUpperCase() : 'FILE';
  }

  protected size(file: File): string {
    return formatSize(file.size);
  }

  private acceptedExtensions(): string[] {
    return this.accept
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .filter((part) => part.startsWith('.'));
  }

  private isAccepted(file: File): boolean {
    const rules = this.accept.split(',').map((part) => part.trim().toLowerCase()).filter(Boolean);
    if (!rules.length) return true;
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();
    return rules.some((rule) =>
      rule.startsWith('.')
        ? name.endsWith(rule)
        : rule.endsWith('/*')
          ? type.startsWith(rule.slice(0, -1))
          : type === rule,
    );
  }
}
